/*
 * NIZAMI: marketing-layer behaviour — ported from the v1 homepage script
 * (git tag homepage-v1-isolated). Powers the [data-nz-slider] carousels, the
 * [data-nz-autorail] drifting rows (reviews), the mobile header menu, and the
 * marquee pause controls.
 * Self-contained, no Dawn/global.js dependency. Loaded by layout/nz-marketing.liquid.
 */
(function () {
  function initSlider(root) {
    var track = root.querySelector('[data-nz-track]');
    var prev = root.querySelector('[data-nz-prev]');
    var next = root.querySelector('[data-nz-next]');
    var current = root.querySelector('[data-nz-current]');
    var total = root.querySelector('[data-nz-total]');
    if (!track) return;

    function step() {
      var first = track.children[0];
      if (!first) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '16') || 16;
      return first.getBoundingClientRect().width + gap;
    }
    function pageInfo() {
      var s = step() || 1;
      var per = Math.max(1, Math.round(track.clientWidth / s));
      var pages = Math.max(1, Math.ceil(track.children.length / per));
      var page = Math.min(pages, Math.round(track.scrollLeft / (s * per)) + 1);
      return { pages: pages, page: page };
    }
    function update() {
      var info = pageInfo();
      if (current) current.textContent = info.page;
      if (total) total.textContent = info.pages;
      var maxScroll = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll;
    }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step() * Math.max(1, Math.round(track.clientWidth / step()) - 0), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step() * Math.max(1, Math.round(track.clientWidth / step()) - 0), behavior: 'smooth' }); });
    track.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function initMenu() {
    var burger = document.querySelector('[data-nz-burger]');
    var nav = document.querySelector('[data-nz-nav]');
    if (!burger || !nav) return;
    function setOpen(open) {
      nav.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('nz-nav-open', open);
    }
    burger.addEventListener('click', function () { setOpen(!nav.classList.contains('is-open')); });
    // Close the drawer when a real link (not a dropdown toggle) is followed.
    nav.addEventListener('click', function (e) {
      if (e.target.closest('a')) setOpen(false);
    });
    // Esc closes the drawer; keep desktop (inline bar) untouched.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) { setOpen(false); burger.focus(); }
    });
  }

  function initMarquees() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return; // animation already disabled in CSS
    var pauseIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';
    var playIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5l11 7-11 7z"/></svg>';
    document.querySelectorAll('.nizami-reviews__marquee, .nz-products__marquee').forEach(function (m) {
      var btn = document.createElement('button');
      btn.className = 'nz-marquee-toggle';
      btn.setAttribute('aria-label', 'Pause auto-scrolling');
      btn.setAttribute('aria-pressed', 'false');
      btn.innerHTML = pauseIcon;
      m.appendChild(btn);
      btn.addEventListener('click', function () {
        var paused = m.classList.toggle('nz-marquee-paused');
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        btn.setAttribute('aria-label', paused ? 'Resume auto-scrolling' : 'Pause auto-scrolling');
        btn.innerHTML = paused ? playIcon : pauseIcon;
      });
    });
  }

  /*
   * [data-nz-autorail="<px per second>"]: a scroll row that drifts slowly on its
   * own AND keeps arrows and swipe working (Adnan 2026-10-29: the refreshed
   * reviews row lost the old marquee's movement). The section renders its items
   * twice, the second set marked [data-nz-clone], so the drift loops seamlessly:
   * once scrollLeft passes one full set it jumps back by that width.
   * When drifting, a third set ([data-nz-clone-before]) is copied in FRONT of the
   * originals and the row starts one set in (Adnan 2026-10-29: "gaps to the left
   * when they first scroll into view"). The strip between the screen edge and
   * the page gutter is then always filled, the first original card still lines
   * up with the gutter, and the loop window [w, 2w) works in both directions.
   * [data-nz-direction="reverse"] drifts the other way.
   * Rails sharing [data-nz-pause-group="<id>"] (the two review rows) share ONE
   * pause button, which sits under the last rail of the group.
   * Pauses while the visitor touches, drags, wheels, focuses inside or uses an
   * arrow, then resumes. No hover pause (Adnan removed it from the marquee on
   * 2026-07-19); the pause button is the persistent control. Reduced motion:
   * no drift, no button, plain swipe row.
   */
  var pauseGroups = {};
  function initAutoRail(rail) {
    var track = rail.querySelector('[data-nz-track]');
    if (!track) return;
    var prev = rail.querySelector('[data-nz-prev]');
    var next = rail.querySelector('[data-nz-next]');
    var speed = parseFloat(rail.getAttribute('data-nz-autorail'));
    if (isNaN(speed)) speed = 40;
    var dir = rail.getAttribute('data-nz-direction') === 'reverse' ? -1 : 1;
    // Speed 0 means arrows and swipe only, no drift.
    // Rows marked data-nz-grid-phone are a static grid on phones (Adnan
    // 2026-10-29): no drift, so no leading clones and no pause button there.
    var gridPhone = rail.hasAttribute('data-nz-grid-phone') && window.matchMedia('(max-width: 767px)').matches;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches || speed <= 0 || gridPhone;

    var clones = track.querySelectorAll('[data-nz-clone]');
    var lead = !reduce && clones.length > 0;
    if (lead) {
      var frag = document.createDocumentFragment();
      clones.forEach(function (c) {
        var b = c.cloneNode(true);
        b.removeAttribute('data-nz-clone');
        b.setAttribute('data-nz-clone-before', '');
        frag.appendChild(b);
      });
      track.insertBefore(frag, track.firstChild);
    }

    // One set's width: from the first original card to its first clone.
    function loopWidth() {
      var clone = track.querySelector('[data-nz-clone]');
      var first = track.querySelector(':scope > :not([data-nz-clone]):not([data-nz-clone-before])');
      return clone && first ? clone.offsetLeft - first.offsetLeft : 0;
    }
    function step() {
      var first = track.children[0];
      var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return first ? first.getBoundingClientRect().width + gap : track.clientWidth;
    }
    // Keep scrollLeft inside the loop window: [w, 2w) with the leading set,
    // [0, w) without it. Positions one set apart show identical cards.
    function wrap() {
      var w = loopWidth();
      if (!w) return;
      var lo = lead ? w : 0;
      if (track.scrollLeft >= lo + w) track.scrollLeft -= w;
      else if (track.scrollLeft < lo || (!lead && track.scrollLeft <= 0)) track.scrollLeft += w;
    }

    var holdUntil = 0, visible = true, pos = track.scrollLeft, last = 0;
    var groupId = rail.getAttribute('data-nz-pause-group');
    var group = groupId ? (pauseGroups[groupId] = pauseGroups[groupId] || { paused: false, btn: null }) : { paused: false, btn: null };
    function hold(ms) { holdUntil = Math.max(holdUntil, performance.now() + ms); }
    function release(ms) { holdUntil = performance.now() + ms; }

    // Re-base BEFORE a smooth arrow scroll, so wrap() never has to jump mid-animation.
    if (prev) prev.addEventListener('click', function () {
      hold(4000);
      var w = loopWidth(), lo = lead ? w : 0;
      if (w && track.scrollLeft - step() < lo) track.scrollLeft += w;
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function () {
      hold(4000);
      var w = loopWidth(), lo = lead ? w : 0;
      if (w && track.scrollLeft + step() >= lo + w) track.scrollLeft -= w;
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });
    track.addEventListener('scroll', wrap, { passive: true });

    // Rows that don't loop (no clones, e.g. the occasions row): each arrow
    // hides at its own end, so the back arrow appears once the row has moved.
    function edges() {
      if (loopWidth()) return;
      var max = track.scrollWidth - track.clientWidth - 4;
      if (prev) prev.classList.toggle('is-hidden', track.scrollLeft <= 4);
      if (next) next.classList.toggle('is-hidden', track.scrollLeft >= max);
    }
    track.addEventListener('scroll', edges, { passive: true });
    window.addEventListener('resize', edges);
    edges();

    if (reduce) return;

    // Snap and smooth scrolling fight a pixel-by-pixel drift, so both go off here.
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    // Start one set in: the first original card sits on the gutter and the
    // leading copy fills the strip to its left.
    track.scrollLeft = loopWidth();

    // Pause only for real interaction with the row. Two things looked like
    // "random" pauses (Adnan 2026-10-29) and are deliberately ignored:
    //  - vertical wheel/trackpad scrolling of the PAGE while the pointer happens
    //    to be over the row (only a sideways wheel counts as using the row);
    //  - mouse focus left on a clicked card, which held the drift until the
    //    visitor clicked elsewhere (only keyboard focus, :focus-visible, counts).
    var lastPointer = 0;
    track.addEventListener('pointerdown', function () { lastPointer = Date.now(); hold(1e9); });
    track.addEventListener('pointerup', function () { release(2500); });
    track.addEventListener('pointercancel', function () { release(2500); });
    track.addEventListener('touchstart', function () { hold(1e9); }, { passive: true });
    track.addEventListener('touchend', function () { release(1500); }, { passive: true });
    track.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) hold(2500);
    }, { passive: true });
    track.addEventListener('focusin', function (e) {
      if (Date.now() - lastPointer < 1000) return; // focus that came from a click or tap
      if (e.target.matches && e.target.matches(':focus-visible')) hold(1e9);
    });
    track.addEventListener('focusout', function () { release(1500); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { visible = entries[0].isIntersecting; }).observe(rail);
    }

    if (!group.btn) {
      var pauseIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';
      var playIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5l11 7-11 7z"/></svg>';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nz-marquee-toggle';
      btn.setAttribute('aria-label', 'Pause auto-scrolling');
      btn.setAttribute('aria-pressed', 'false');
      btn.innerHTML = pauseIcon;
      btn.addEventListener('click', function () {
        group.paused = !group.paused;
        btn.setAttribute('aria-pressed', group.paused ? 'true' : 'false');
        btn.setAttribute('aria-label', group.paused ? 'Resume auto-scrolling' : 'Pause auto-scrolling');
        btn.innerHTML = group.paused ? playIcon : pauseIcon;
      });
      group.btn = btn;
    }
    rail.appendChild(group.btn); // moves it under each later rail of the group, so it ends under the last

    function tick(t) {
      window.requestAnimationFrame(tick);
      var dt = last ? Math.min(64, t - last) : 0;
      last = t;
      if (group.paused || !visible || t < holdUntil) { pos = track.scrollLeft; return; }
      // Accumulate in floats: browsers round scrollLeft, so adding 0.6px a frame
      // to scrollLeft itself would never move.
      pos += dir * speed * dt / 1000;
      var w = loopWidth();
      if (w) {
        var lo = lead ? w : 0;
        if (pos >= lo + w) pos -= w;
        else if (pos < lo) pos += w;
      }
      track.scrollLeft = pos;
    }
    window.requestAnimationFrame(tick);
  }

  /*
   * video[data-nz-video][data-src]: a muted, looping, decorative clip (the "Cut
   * the way you cook" band). The file loads only when the video nears the
   * screen, plays while visible and pauses off screen. A pause button (the
   * carousels' toggle) is the persistent control. Reduced motion: the poster
   * stays, nothing autoplays, and the button offers play instead.
   */
  function initVideo(video) {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Data saver on (common on Pakistani mobile plans): poster only, play on request.
    var saveData = !!(navigator.connection && navigator.connection.saveData);
    var paused = reduce || saveData, visible = false;
    var pauseIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';
    var playIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5l11 7-11 7z"/></svg>';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nz-marquee-toggle';
    video.parentNode.appendChild(btn);

    function load() {
      if (!video.getAttribute('src') && video.dataset.src) video.src = video.dataset.src;
    }
    function sync() {
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      btn.innerHTML = paused ? playIcon : pauseIcon;
      if (!paused && visible) {
        load();
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        video.pause();
      }
    }
    btn.addEventListener('click', function () { paused = !paused; sync(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        sync();
      }, { rootMargin: '200px 0px' }).observe(video);
    } else {
      visible = true;
    }
    sync();
  }

  function boot() {
    document.querySelectorAll('[data-nz-slider]').forEach(initSlider);
    document.querySelectorAll('[data-nz-autorail]').forEach(initAutoRail);
    document.querySelectorAll('video[data-nz-video]').forEach(initVideo);
    initMenu();
    initMarquees();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
